import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { DEFAULT_LANG, translations } from './translations';

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: (key) => key,
});

const STORAGE_KEY = 'aitts_language';
const SUPPORTED_LANGS = Object.keys(translations);

const getInitialLang = () => {
  if (typeof window === 'undefined') return DEFAULT_LANG;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && SUPPORTED_LANGS.includes(stored)) {
    return stored;
  }
  return DEFAULT_LANG;
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(getInitialLang);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch (error) {
      // ignore persistence errors
    }
  }, [lang]);

  const t = useMemo(() => {
    const current = translations[lang] || translations[DEFAULT_LANG];
    return (key) => current?.[key] ?? translations[DEFAULT_LANG]?.[key] ?? key;
  }, [lang]);

  const setLang = (value) => {
    if (!SUPPORTED_LANGS.includes(value)) return;
    setLangState(value);
  };

  const value = useMemo(() => ({
    lang,
    setLang,
    t,
  }), [lang, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);

export default LanguageContext;
